import React from 'react';

// Layout
import LayoutPages from '../../layout/LayoutPages';

// Kit
import { PageHeader, Card, Button } from '@/components/ui';

// Icons
import {
    BookOpen,
    PlayCircle,
    MessageCircle,
    LifeBuoy,
    Mail,
    Calendar,
    ChevronDown,
    ChevronRight,
} from 'lucide-react';

const resources = [
    {
        icon: BookOpen,
        title: 'Guías de configuración',
        description: 'Aprendé a configurar tus asesores, horarios y mensajes paso a paso.',
        cta: 'Ver guías',
        href: 'https://ayuda.whatspro.com',
    },
    {
        icon: PlayCircle,
        title: 'Video tutoriales',
        description: 'Mirá tutoriales cortos para sacarle el máximo provecho a WhatsPRO.',
        cta: 'Ver videos',
        href: 'https://ayuda.whatspro.com/tutoriales',
    },
    {
        icon: MessageCircle,
        title: 'Preguntas frecuentes',
        description: 'Resolvé las dudas más comunes sobre la plataforma en segundos.',
        cta: 'Ver preguntas',
        href: '#faq',
    },
];

const faqs = [
    {
        question: '¿Cómo agrego un nuevo asesor?',
        answer: 'Ingresá a la sección "Asesores" desde el menú y tocá el botón "Agregar asesor". Completá los datos personales, los horarios de atención, los mensajes automáticos y las vías de comunicación en cada paso del asistente.',
    },
    {
        question: '¿Cómo instalo el botón de WhatsPRO en mi tienda?',
        answer: 'Desde "Integraciones" vas a encontrar el código y las instrucciones para conectar WhatsPRO con tu plataforma. Copiá el fragmento e insertalo en tu sitio, o seguí la integración guiada según tu tienda.',
    },
    {
        question: '¿Puedo configurar horarios de atención personalizados?',
        answer: 'Sí. Al crear o editar un asesor, en el paso "Horarios" podés activar los horarios personalizados y definir franjas por día. Fuera de ese horario se muestra el mensaje automático que configures.',
    },
    {
        question: '¿Cómo cambio los mensajes automáticos?',
        answer: 'En el paso "Mensajes" del asistente de asesores podés editar el mensaje de bienvenida y el mensaje que se envía fuera del horario de atención.',
    },
    {
        question: '¿Dónde gestiono mi suscripción y mis pagos?',
        answer: 'Toda la información de tu plan, facturas y método de pago está disponible en la sección "Facturación".',
    },
];

const HelpScreen = () => {
    return (
        <LayoutPages>
            <div className="mx-auto w-full max-w-5xl text-left">
                <PageHeader
                    eyebrow="Ayuda"
                    title="Centro de ayuda"
                    subtitle="Encontrá guías, tutoriales y respuestas rápidas. Si necesitás una mano, nuestro equipo está para ayudarte."
                />

                {/* Recursos */}
                <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {resources.map(({ icon: Icon, title, description, cta, href }) => (
                        <a
                            key={title}
                            href={href}
                            target={href.startsWith('#') ? undefined : '_blank'}
                            rel="noreferrer"
                            className="group block no-underline"
                        >
                            <Card hover className="flex h-full flex-col p-5">
                                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-sm bg-primary-tint text-primary [&>svg]:size-[22px]">
                                    <Icon />
                                </div>
                                <h3 className="text-[15px] font-bold leading-tight tracking-[-0.01em] text-foreground">
                                    {title}
                                </h3>
                                <p className="mt-1.5 flex-1 text-[13px] leading-relaxed text-muted-foreground">
                                    {description}
                                </p>
                                <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary transition-transform group-hover:gap-1.5">
                                    {cta}
                                    <ChevronRight className="size-4" />
                                </span>
                            </Card>
                        </a>
                    ))}
                </section>

                {/* Preguntas frecuentes */}
                <section id="faq" className="mt-10">
                    <h2 className="mb-4 text-lg font-bold tracking-[-0.01em] text-foreground">
                        Preguntas frecuentes
                    </h2>
                    <div className="flex flex-col gap-3">
                        {faqs.map(({ question, answer }) => (
                            <details
                                key={question}
                                className="group rounded-lg border border-border bg-card shadow-card transition-shadow open:shadow-elevate"
                            >
                                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-[15px] font-semibold text-foreground [&::-webkit-details-marker]:hidden">
                                    {question}
                                    <ChevronDown className="size-5 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180" />
                                </summary>
                                <div className="px-5 pb-4 text-sm leading-relaxed text-muted-foreground">
                                    {answer}
                                </div>
                            </details>
                        ))}
                    </div>
                </section>

                {/* Contacto */}
                <section className="mt-10">
                    <Card className="overflow-hidden">
                        <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
                            <div className="flex items-start gap-4">
                                <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-sm bg-primary-tint text-primary [&>svg]:size-6 sm:flex">
                                    <LifeBuoy />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold tracking-[-0.01em] text-foreground">
                                        ¿Necesitás ayuda personalizada?
                                    </h2>
                                    <p className="mt-1 max-w-md text-sm text-muted-foreground">
                                        Escribinos o agendá una llamada con nuestro equipo de soporte. Te respondemos en el día.
                                    </p>
                                </div>
                            </div>
                            <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                                <Button asChild className="no-underline">
                                    <a href="mailto:soporte@whatspro.com">
                                        <Mail />
                                        Escribir a soporte
                                    </a>
                                </Button>
                                <Button asChild variant="outline" className="no-underline">
                                    <a href="https://calendly.com" target="_blank" rel="noreferrer">
                                        <Calendar />
                                        Agendar una llamada
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </Card>
                </section>
            </div>
        </LayoutPages>
    )
}

export default HelpScreen;
